// SignupPage.jsx
import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';
const HEADER_BG = '#1e40af';

export default function SignupPage() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;

  const [form, setForm] = useState({
    name: '',
    email: '',
    organisation: '',
    role: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullname: form.name,
          email: form.email,
          organisation: form.organisation,
          role: form.role,
          comments: form.message,
        }),
      });

      if (!res.ok) {
        throw new Error(`Signup failed: ${res.status}`);
      }

      setSubmitted(true);

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFF' }}>
      <img src="/ssiconfull.png" alt="logo" style={{ position:'fixed', left:'10px', top:'10px', width:'160px', cursor:'pointer'}} onClick={() => navigate('/')}/>
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 1 } }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Box sx={{py: 1}}>

            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: 34, md: 52 },
                lineHeight: 1.1,
                mb: 2,
              }}
            >
              Start building clinical simulations.
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                fontSize: { xs: 17, md: 20 },
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Tell us a bit about yourself and we&apos;ll get in touch to set you up with SimSuite.
            </Typography>
          </Box>

          {submitted ? (
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: 620,
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 24px 70px rgba(30, 64, 175, 0.12)',
                p: { xs: 3, md: 5 },
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: 28, mb: 1 }}>
                Thank you
              </Typography>

              <Typography color="text.secondary" sx={{ fontSize: 16 }}>
                Your request has been submitted. We&apos;ll be in touch shortly.
              </Typography>

              <Typography color="text.secondary" sx={{ fontSize: 13, mt: 2 }}>
                Redirecting you back to the main page...
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: '100%',
                maxWidth: 620,
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: 'white',
                boxShadow: '0 24px 70px rgba(30, 64, 175, 0.12)',
              }}
            >
              <Box
                sx={{
                  bgcolor: ACCENT,
                  borderBottom: `1px solid ${ACCENT_BORDER}`,
                  px: 3,
                  py: 2,
                  textAlign: 'left',
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 22 }}>
                  Your details
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                  We&apos;ll contact you about access and setup.
                </Typography>
              </Box>

              <Stack spacing={2.25} sx={{ p: { xs: 2.5, md: 3 } }}>
                <TextField
                  label="Full name"
                  value={form.name}
                  onChange={handleChange('name')}
                  fullWidth
                  required
                />

                <TextField
                  label="Email address"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  fullWidth
                  required
                />

                <TextField
                  label="Organisation / institution"
                  value={form.organisation}
                  onChange={handleChange('organisation')}
                  fullWidth
                  required
                />

                <TextField
                  label="Role"
                  value={form.role}
                  onChange={handleChange('role')}
                  fullWidth
                  placeholder="e.g. simulation lead, doctor, medical student"
                />

                <TextField
                  label="Anything else?"
                  value={form.message}
                  onChange={handleChange('message')}
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Tell us what you’re hoping to use SimSuite for..."
                />

                {error && (
                  <Typography color="error" sx={{ fontSize: 14 }}>
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    py: 1.3,
                    fontWeight: 800,
                  }}
                >
                  {loading ? 'Submitting...' : 'Submit request'}
                </Button>

                <Typography color="text.secondary" sx={{ fontSize: 13, textAlign: 'center' }}>
                  Already have an account?{' '}
                  <Box
                    component="span"
                    onClick={() => navigate('/login')}
                    sx={{
                      color: HEADER_BG,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Log in
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  By submitting, you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                  and{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
}